'use client';

import { useEffect, useState, useCallback } from 'react';
import { MonoIcon } from '@/components/ui/MonoIcon';
import { workspaceApi } from '@/lib/api/workspace/workspaceApi';
import { toast } from 'sonner';
import type { Priority } from 'dico-types';
import { PickListSection } from '@/components/settings/PickListSection';

interface PrioritiesTabContentProps {
  workspaceId: string;
}

export function PrioritiesTabContent({ workspaceId }: PrioritiesTabContentProps) {
  const [priorities, setPriorities] = useState<Priority[]>([]);
  const [loadState, setLoadState] = useState<'idle' | 'loading' | 'loaded'>('idle');

  const load = useCallback(async () => {
    setLoadState('loading');
    try {
      const prioritiesRes = await workspaceApi.getWorkspacePriorities(workspaceId);
      setPriorities(prioritiesRes.data ?? []);
      setLoadState('loaded');
    } catch {
      toast.error('Failed to load priorities');
      setLoadState('loaded');
    }
  }, [workspaceId]);

  useEffect(() => {
    load();
  }, [load]);

  if (loadState === 'loading') {
    return (
      <div className="flex min-h-[200px] items-center justify-center">
        <MonoIcon type="Loader2" className="text-muted-foreground animate-spin" />
      </div>
    );
  }

  return (
    <div className="w-3xl mx-auto min-h-full flex-1 space-y-10 px-8 py-10 xl:border-x">
      <PickListSection
        title="Priorities"
        description="Priority levels for tickets (e.g. Low, High, Urgent)."
        items={priorities}
        showIcon
        defaultIconName="SignalZero"
        defaultNewItemName="New priority"
        onAdd={async (payload) => {
          const res = await workspaceApi.createPriority(workspaceId, payload);
          if (res.success && res.data) {
            setPriorities((prev) => [...prev, res.data!]);
            toast.success('Priority added');
            return res.data.id;
          }
          toast.error(res.error ?? 'Failed to add priority');
          return null;
        }}
        onUpdate={async (id, payload) => {
          const res = await workspaceApi.updatePriority(workspaceId, id, payload);
          if (res.success && res.data) {
            setPriorities((prev) => prev.map((p) => (p.id === id ? res.data! : p)));
            toast.success('Priority updated');
          } else {
            toast.error(res.error ?? 'Failed to update priority');
          }
        }}
        onDelete={async (id) => {
          const res = await workspaceApi.deletePriority(workspaceId, id);
          if (res.success) {
            setPriorities((prev) => prev.filter((p) => p.id !== id));
            toast.success('Priority removed');
          } else {
            toast.error(res.error ?? 'Failed to remove priority');
          }
        }}
      />
    </div>
  );
}
